'use client';

import React from 'react';
import { EventDTO } from '@/data/dtos/Events/EventDTO';
import {Link} from "@/i18n/routing";
import ReactMarkdown from "react-markdown";
import {useLocale, useTranslations} from "next-intl";


function EventStatusBadge({ eventDTO }: {eventDTO: EventDTO }) {
	const t = useTranslations('page.events');

	if (eventDTO.isPassed()) {
		return (
			<div className="badge bg-warning bg-opacity-50 text-bg-warning p-2 me-2">
				{t('status.passed_short')}
			</div>
		);
	} else if (eventDTO.spotsLeft === 0) {
		return (
			<div className="badge text-bg-warning p-2 me-2">
				{t('status.sold_out_short')}
			</div>
		);
	} else if (eventDTO.isOngoing()) {
		return (
			<div className="badge text-bg-info p-2 me-2">
				{t('status.now_short')}
			</div>
		);
	}
	return null;
};


function EventCard({ eventDTO }: { eventDTO: EventDTO }) {
	const t = useTranslations('page.events');


	eventDTO.instructors.sort((a, b) => a.name.localeCompare(b.name));

	const MAX_VISIBLE_INSTRUCTORS = 3;

	return (
		<div className="row">
			<div className="col-12 mb-0 mb-md-1">
				<div className="d-flex justify-content-between">
					<div className="d-inline-block">
						<div className="mb-2 mb-md-1">
							<EventStatusBadge eventDTO={eventDTO} />

							<span className="text-uppercase">{eventDTO.getDates()}</span>
						</div>
						<h2 className=" h6 text-uppercase">
							<Link href={eventDTO.siteLink}>
								{eventDTO.title}
							</Link>
						</h2>
					</div>
					<Link href="#" className="btn btn-light btn-icon rounded-circle mb-0" data-bs-toggle="tooltip" data-placement="top" data-bs-title={t('card.favorite_action')}>
						<i className="bi bi-heart"></i>
					</Link>
				</div>
			</div>
			<div className="col-12 col-md-8 mx-auto text-start">
				{eventDTO.categories && (
					<ul className="list-inline mb-1 mb-md-2">
						{eventDTO.categories.map((categoryDTO) => (
							<li key={categoryDTO.slug} className="list-inline-item">
								<Link className="btn btn-outline-light btn-sm" href={categoryDTO.siteLink}>
									{categoryDTO.name}
								</Link>
							</li>
						))}
					</ul>
				)}

				{!eventDTO.isPassed() && eventDTO.showSpotsLeft() && (
					<div className="badge text-bg-primary mb-1 mb-md-2 p-2">
						<i className="bi bi-patch-exclamation-fill me-2"></i>
						{t('card.spots_left', {spots_left: eventDTO.spotsLeft})}
					</div>
				)}

				<ul className="list-group list-group-borderless border-0 mb-2">
					<li className="list-group-item d-flex mb-0">
						{!eventDTO.getOrganizer().verified ? (
							eventDTO.getOrganizer().name
						) : (
							<>
								<i className="bi bi-patch-check-fill text-primary me-2"></i>
								<Link href={eventDTO.getOrganizer().siteLink}>
									{eventDTO.getOrganizer().name}
								</Link>
							</>
						)}
					</li>

					<li className="list-group-item d-flex mb-0">
						<i className="bi bi-geo-alt me-2"></i>
						{eventDTO.getLocation().summaryAddress}
					</li>
					<li className="list-group-item d-flex mb-0">
						<i className="bi bi bi-cash me-2"></i>
						{
							eventDTO.getPriceLabel(
								t('card.price_free'),
								t('card.price_starting_at')
							)
						}
					</li>
				</ul>

				<a className="text-black fw-semibold d-none d-md-block mt-4" data-bs-toggle="collapse" href={'#e-' + eventDTO.id} role="button" aria-expanded="false" aria-controls={'e-' + eventDTO.id}>
					{t('card.show_more_info')}
					<i className="bi bi-chevron-down ms-1"></i>
				</a>
			</div>

			{/* Desktop Avatars*/}
			<div className="col-12 col-md-4 mt-3 d-none d-md-block">
				<ul className="avatar-group mb-3">
					{eventDTO.instructors.slice(0, MAX_VISIBLE_INSTRUCTORS).map((instructorDTO) => (
						<li key={instructorDTO.id} className="avatar avatar-xl flex-shrink-0">
							<Link className="" href={instructorDTO.siteLink}>
								<img className="avatar-img rounded-circle" src={instructorDTO.avatar} alt={instructorDTO.nameWithPrefix} />
							</Link>
						</li>
					))}
					{eventDTO.instructors.length > MAX_VISIBLE_INSTRUCTORS && (
						<li className="avatar avatar-xl flex-shrink-0">
							<div className="avatar-img rounded-circle bg-black">
								<span className="text-white position-absolute top-50 start-50 translate-middle fw-bold fs-3">
									+{eventDTO.instructors.length - MAX_VISIBLE_INSTRUCTORS}
								</span>
							</div>
						</li>
					)}
				</ul>

				<ul className="list-group list-group-borderless border-0 mb-2">
					{eventDTO.instructors.slice(0, MAX_VISIBLE_INSTRUCTORS).map((instructorDTO) => (
						<li key={instructorDTO.id} className="list-group-item d-flex mb-0 p-0">
							<h3 className="mb-1 small">
								<Link className="" href={instructorDTO.siteLink}>
									{instructorDTO.nameWithPrefix}
								</Link>
							</h3>
						</li>
					))}
					{eventDTO.instructors.length > MAX_VISIBLE_INSTRUCTORS && (
						<>
							<a className="text-black fw-semibold d-none d-md-block" data-bs-toggle="collapse" href={'#instructors-' + eventDTO.id} role="button" aria-expanded="false" aria-controls={'instructors-' + eventDTO.id}>
								{t('card.show_all_lectors')}
								<i className="bi bi-chevron-down ms-1"></i>
							</a>
							<div className="collapse" id={'instructors-' + eventDTO.id}>
								{eventDTO.instructors.slice(MAX_VISIBLE_INSTRUCTORS).map((instructorDTO) => (
									<li key={instructorDTO.id} className="list-group-item d-flex mb-0 p-0">
										<h3 className="mb-1 small">
											<Link className="" href={instructorDTO.siteLink}>
												{instructorDTO.nameWithPrefix}
											</Link>
										</h3>
									</li>
								))}
							</div>
						</>
					)}
				</ul>
			</div>

			{/* Mobile Avatars*/}
			<div className="col-12 col-md-3 mt-3 d-block d-md-none">
				{eventDTO.instructors.slice(0, MAX_VISIBLE_INSTRUCTORS).map((instructorDTO) => (
					<div key={instructorDTO.id} className="d-flex align-items-sm-center mb-3">
						<div className="avatar avatar-xl flex-shrink-0">
							<Link className="heading-color" href={instructorDTO.siteLink}>
								<img className="avatar-img rounded-circle" src={instructorDTO.avatar} alt={instructorDTO.nameWithPrefix} />
							</Link>
						</div>
						<div className="ms-3">
							<div className="mb-1">
								<Link className="heading-color" href={instructorDTO.siteLink}>
									{instructorDTO.nameWithPrefix}
								</Link>
							</div>
							<p className="mb-0">{instructorDTO.currentPosition}</p>
						</div>
					</div>
				))}
				{eventDTO.instructors.length > MAX_VISIBLE_INSTRUCTORS && (
					<>
						<a className="text-black fw-semibold mt-4" data-bs-toggle="collapse" href={'#mobile-instructors-' + eventDTO.id} role="button" aria-expanded="false" aria-controls={'mobile-instructors-' + eventDTO.id}>
							{t('card.show_all_lectors')}
							<i className="bi bi-chevron-down ms-1"></i>
						</a>
						<div className="collapse" id={'mobile-instructors-' + eventDTO.id}>
							{eventDTO.instructors.slice(MAX_VISIBLE_INSTRUCTORS).map((instructorDTO) => (
								<div key={instructorDTO.id} className="d-flex align-items-sm-center mb-3">
									<div className="avatar avatar-xl flex-shrink-0">
										<Link className="heading-color" href={instructorDTO.siteLink}>
											<img className="avatar-img rounded-circle" src={instructorDTO.avatar} alt={instructorDTO.nameWithPrefix} />
										</Link>
									</div>
									<div className="ms-3">
										<div className="mb-1">
											<Link className="heading-color" href={instructorDTO.siteLink}>
												{instructorDTO.nameWithPrefix}
											</Link>
										</div>
										<p className="mb-0">{instructorDTO.currentPosition}</p>
									</div>
								</div>
							))}
						</div>
					</>
				)}
			</div>

			<div className="col-12 mt-4 d-md-none d-block">
				<a className="fw-semibold text-black" data-bs-toggle="collapse" href={'#e-' + eventDTO.id} role="button" aria-expanded="false" aria-controls={'e-' + eventDTO.id}>
					{t('card.show_more_info')}
					<i className="bi bi-chevron-down ms-1"></i>
				</a>
			</div>

			<div className="col-12 mt-4 collapse" id={'e-' + eventDTO.id}>
				<ReactMarkdown
					components={{
						p: ({node, ...props}) => <p className="mb-2" {...props} />,
						h3: ({node, ...props}) => <h3 className="h5" {...props} />,
						h4: ({node, ...props}) => <h3 className="h6" {...props} />,
					}}
				>
					{eventDTO.description}
				</ReactMarkdown>
			</div>
		</div>
	);
}

export default function EventListClient({ events, plainEvents }: { events?: EventDTO[], plainEvents?: Record<string, any>[] }) {
    const locale = useLocale();

    if (!events && !plainEvents) {
		return;
    }

	if (!events && plainEvents) {
	    events = plainEvents.map((event) =>
	        EventDTO.fromArray(event, locale)
	    );
	}

	// Ensure events is defined before filtering
	events = (events ?? []).filter(event => event.isVisible());

	if (!events.length) {
		return;
	}

	return (
		<div className="col-12">
			{events.map((eventDTO, index) => (
				<div key={eventDTO.id}>
					<EventCard eventDTO={eventDTO} />
					{index < events.length - 1 && <hr className="my-5" />}
				</div>
			))}
		</div>
	);
}
